import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { formatTimeElapsed, TimeInput, FormatTimeElapsedOptions } from './formatTimeElapsed';

dayjs.extend(utc);

export interface FormatTimeRemainingOptions {
  /** Maximum number of units to display. Defaults to 2. */
  maxUnits?: number;
  /** Value to return when no meaningful duration can be computed. Defaults to null. */
  fallback?: string | null;
}

/**
 * Formats the remaining time from now until a future UTC date.
 * Returns null (or custom fallback) if the date is null, invalid, or already in the past.
 *
 * @param utcDate - A UTC date/time value representing the future deadline
 * @param options - Configuration options
 * @returns Formatted remaining time string, or null if not applicable
 *
 * @example
 * // 45 minutes from now
 * formatTimeRemaining('2026-02-16T12:45:00') // "45 minutes"
 *
 * @example
 * // 2 hours and 30 minutes from now
 * formatTimeRemaining('2026-02-16T14:30:00') // "2 hours and 30 minutes"
 *
 * @example
 * // Already in the past
 * formatTimeRemaining('2026-02-16T08:00:00') // null
 */
export const formatTimeRemaining = (
  utcDate: TimeInput,
  options: FormatTimeRemainingOptions = {}
): string | null => {
  const { maxUnits = 2, fallback = null } = options;

  if (!utcDate) {
    return fallback;
  }

  const target = dayjs.utc(utcDate).local();
  const now = dayjs();

  if (!target.isValid() || !target.isAfter(now)) {
    return fallback;
  }

  // Use formatTimeElapsed with now as start and target as end, without suffix ("ago")
  const result = formatTimeElapsed(now.toISOString(), target.toISOString(), {
    maxUnits,
    includeSuffix: false,
    fallback: fallback ?? '—'
  });

  // If formatTimeElapsed returned the fallback, propagate null
  if (result === fallback || result === '—') {
    return fallback;
  }

  return result;
};

export default formatTimeRemaining;
